import './ChannelPerformanceBars.scss';
import { useMemo, useState } from 'react';
import { ChartTooltipV2 } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { Neutral500 } from '@src/utils/colors';
import {
  BASELINE_DISPLAY_NAME,
  BASELINE_GROUP_KEY,
  BASELINE_TOGGLE_LABEL,
  DARK_CHANNEL_TOGGLE_LABEL,
  SPEND_BAR_OPACITY,
  TWO_STAGE_ADDED_LABEL,
  TWO_STAGE_REDUCED_LABEL
} from '../dtc-commerce-mmm-constants';
import { useChannelColor } from '../context/DtcCommerceMmmDataContext';
import { DashboardData, HierarchyLevel } from '../dtc-commerce-mmm-types';
import { aggregateDualChannels, DualChannel } from '../hooks/useChannelAggregation';
import {
  formatCurrency,
  formatNumberCompact,
  formatPercentage
} from '../utils/dtc-commerce-mmm-format';
import { ChannelIcon } from './ChannelIcon';
import { CursorTooltipPopover, useCursorTooltip } from './CursorTooltip';
import { IncrementalityBeaker } from './IncrementalityBeaker';
import { IncrementalityNote } from './IncrementalityNote';
import { ChannelTooltipSubheader } from './ChannelTooltipSubheader';
import { MediaTypeBadge, MediaTypeBadgeVariant } from './MediaTypeBadge';
import { TwoStageBar } from './TwoStageBar';

interface ChannelPerformanceBarsProps {
  data: DashboardData;
  isRevenue: boolean;
  hierarchyLevel: HierarchyLevel;
  onChannelClick: (groupKey: string, focusedFeatureName?: string) => void;
}

const SortOption = {
  SPEND_DESC: 'spend_desc',
  SPEND_ASC: 'spend_asc',
  CONTRIB_DESC: 'contrib_desc',
  CONTRIB_ASC: 'contrib_asc',
  EFFICIENCY_DESC: 'efficiency_desc',
  EFFICIENCY_ASC: 'efficiency_asc',
  ALPHA_DESC: 'alpha_desc',
  ALPHA_ASC: 'alpha_asc'
} as const;
type SortOption = (typeof SortOption)[keyof typeof SortOption];

const ValueDisplay = {
  PERCENT: 'percent',
  ACTUAL: 'actual'
} as const;
type ValueDisplay = (typeof ValueDisplay)[keyof typeof ValueDisplay];

const VALUE_DISPLAY_OPTIONS = [
  { value: ValueDisplay.ACTUAL, label: 'Actual Values' },
  { value: ValueDisplay.PERCENT, label: 'Percent' }
];

const formatEfficiency = (value: number, isRevenue: boolean): string => {
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  if (isRevenue) return formatted;
  return `$${formatted}`;
};

const compareForSort = (a: DualChannel, b: DualChannel, option: SortOption): number => {
  switch (option) {
    case SortOption.SPEND_DESC:
      return b.actualizedSpend - a.actualizedSpend;
    case SortOption.SPEND_ASC:
      return a.actualizedSpend - b.actualizedSpend;
    case SortOption.CONTRIB_DESC:
      return b.actualizedContribution - a.actualizedContribution;
    case SortOption.CONTRIB_ASC:
      return a.actualizedContribution - b.actualizedContribution;
    case SortOption.EFFICIENCY_DESC:
    case SortOption.EFFICIENCY_ASC: {
      const aVal = a.efficiencyActualized ?? -Infinity;
      const bVal = b.efficiencyActualized ?? -Infinity;
      const desc = option === SortOption.EFFICIENCY_DESC;
      return desc ? bVal - aVal : aVal - bVal;
    }
    case SortOption.ALPHA_ASC:
      return a.displayName.localeCompare(b.displayName);
    case SortOption.ALPHA_DESC:
    default:
      return b.displayName.localeCompare(a.displayName);
  }
};

interface RowTooltipProps {
  channel: DualChannel;
  hierarchyLevel: HierarchyLevel;
  isRevenue: boolean;
  showPercent: boolean;
}

const RowTooltipBody = ({ channel, hierarchyLevel, isRevenue, showPercent }: RowTooltipProps) => {
  const colorFor = useChannelColor();
  const formatContribution = isRevenue ? formatCurrency : formatNumberCompact;
  const actualizedSpendValue = showPercent ? channel.actualizedSpendShare : channel.actualizedSpend;
  const actualizedContributionValue = showPercent
    ? channel.actualizedContributionShare
    : channel.actualizedContribution;
  const spendDelta =
    actualizedSpendValue - (showPercent ? channel.directSpendShare : channel.directSpend);
  const contributionDelta =
    actualizedContributionValue -
    (showPercent ? channel.directContributionShare : channel.directContribution);

  const formatSpend = (v: number) => (showPercent ? formatPercentage(v, 1) : formatCurrency(v));
  const formatContrib = (v: number) =>
    showPercent ? formatPercentage(v, 1) : formatContribution(v);
  const formatDelta = (v: number, kind: 'spend' | 'contribution'): string => {
    if (Math.abs(v) < 0.05) return '—';
    const sign = v > 0 ? '+' : '−';
    const abs = Math.abs(v);
    if (showPercent) return `${sign}${formatPercentage(abs, 1)}`;
    const fmt = kind === 'spend' || isRevenue ? formatCurrency : formatContribution;
    return `${sign}${fmt(abs)}`;
  };

  const channelColor = colorFor(channel.channel);
  const kpiLabel = isRevenue ? 'Revenue' : 'Conv.';
  const efficiencyLabel =
    channel.efficiencyActualized !== null
      ? formatEfficiency(channel.efficiencyActualized, isRevenue)
      : '—';

  return (
    <ChartTooltipV2
      headers={[channel.displayName]}
      subHeaderLabel={
        <ChannelTooltipSubheader
          channel={channel.channel}
          platform={channel.platform}
          mediaType={channel.mediaType}
          hierarchyLevel={hierarchyLevel}
          hasIncrementalityTest={channel.hasIncrementalityTest}
        />
      }
      items={[
        {
          label: isRevenue ? 'ROAS' : 'CPA',
          value: efficiencyLabel,
          color: 'transparent'
        },
        {
          label: 'Actualized spend',
          value: formatSpend(actualizedSpendValue),
          color: channelColor
        },
        {
          label: `Actualized ${kpiLabel.toLowerCase()}`,
          value: formatContrib(actualizedContributionValue),
          color: channelColor
        },
        {
          label: 'Δ spend vs direct',
          value: formatDelta(spendDelta, 'spend'),
          color: 'transparent'
        },
        {
          label: `Δ ${kpiLabel.toLowerCase()} vs direct`,
          value: formatDelta(contributionDelta, 'contribution'),
          color: 'transparent'
        }
      ]}
    />
  );
};

interface ChannelRowProps {
  channel: DualChannel;
  maxSpend: number;
  maxContribution: number;
  hierarchyLevel: HierarchyLevel;
  isRevenue: boolean;
  showPercent: boolean;
  showCI: boolean;
  contributionShareScale: number;
  onClick: () => void;
}

const ChannelRow = ({
  channel,
  maxSpend,
  maxContribution,
  hierarchyLevel,
  isRevenue,
  showPercent,
  showCI,
  contributionShareScale,
  onClick
}: ChannelRowProps) => {
  const { rowRef, show, target, handlers } = useCursorTooltip();
  const colorFor = useChannelColor();
  const isBaseline = channel.groupKey === BASELINE_GROUP_KEY;
  const color = isBaseline ? Neutral500 : colorFor(channel.channel);
  const formatContribution = isRevenue ? formatCurrency : formatNumberCompact;

  const spendLabel = showPercent
    ? formatPercentage(channel.actualizedSpendShare, 1)
    : formatCurrency(channel.actualizedSpend);
  const contributionLabel = showPercent
    ? formatPercentage(channel.actualizedContributionShare, 1)
    : formatContribution(channel.actualizedContribution);

  if (isBaseline) {
    return (
      <>
        <div className='cpb-row cpb-row-baseline' ref={rowRef} {...handlers}>
          <div className='cpb-row-label'>
            <span className='cpb-row-label-icon-spacer' aria-hidden='true' />
            <span className='cpb-row-label-text cpb-row-label-text-baseline'>
              {channel.displayName}
            </span>
          </div>
          <div className='cpb-row-chart'>
            <div className='cpb-bar-row cpb-bar-row-baseline-spend'>
              <TwoStageBar direct={0} actualized={0} maxValue={0} color={color} />
              <span className='cpb-bar-label'>—</span>
            </div>
            <div className='cpb-bar-row'>
              <TwoStageBar
                direct={showPercent ? channel.directContributionShare : channel.directContribution}
                actualized={
                  showPercent ? channel.actualizedContributionShare : channel.actualizedContribution
                }
                maxValue={maxContribution}
                color={color}
              />
              <span className='cpb-bar-label'>{contributionLabel}</span>
            </div>
          </div>
        </div>
        <CursorTooltipPopover id={`cpb-tt-${channel.groupKey}`} show={show} target={target}>
          <ChartTooltipV2
            headers={[channel.displayName]}
            items={[
              {
                label: isRevenue ? 'Revenue' : 'Conv.',
                value: contributionLabel,
                color
              }
            ]}
          />
        </CursorTooltipPopover>
      </>
    );
  }

  return (
    <>
      <InteractiveDiv className='cpb-row' onClick={onClick} ref={rowRef} {...handlers}>
        <div className='cpb-row-label'>
          <ChannelIcon
            channel={channel.channel}
            platform={channel.platform}
            level={hierarchyLevel}
            size={16}
          />
          <span className='cpb-row-label-text'>{channel.displayName}</span>
          {channel.hasIncrementalityTest && <IncrementalityBeaker />}
          <MediaTypeBadge mediaType={channel.mediaType} variant={MediaTypeBadgeVariant.SHORT} />
        </div>
        <div className='cpb-row-chart'>
          <div className='cpb-bar-row'>
            <TwoStageBar
              direct={showPercent ? channel.directSpendShare : channel.directSpend}
              actualized={showPercent ? channel.actualizedSpendShare : channel.actualizedSpend}
              maxValue={maxSpend}
              color={color}
              spendOpacity={SPEND_BAR_OPACITY}
            />
            <span className='cpb-bar-label'>{spendLabel}</span>
          </div>
          <div className='cpb-bar-row'>
            <TwoStageBar
              direct={showPercent ? channel.directContributionShare : channel.directContribution}
              actualized={
                showPercent ? channel.actualizedContributionShare : channel.actualizedContribution
              }
              maxValue={maxContribution}
              color={color}
              credibleInterval={
                showCI
                  ? {
                      lo: showPercent
                        ? channel.actualizedContributionCiLo * contributionShareScale
                        : channel.actualizedContributionCiLo,
                      hi: showPercent
                        ? channel.actualizedContributionCiHi * contributionShareScale
                        : channel.actualizedContributionCiHi
                    }
                  : undefined
              }
            />
            <span className='cpb-bar-label'>{contributionLabel}</span>
          </div>
        </div>
      </InteractiveDiv>
      <CursorTooltipPopover id={`cpb-tt-${channel.groupKey}`} show={show} target={target}>
        <RowTooltipBody
          channel={channel}
          hierarchyLevel={hierarchyLevel}
          isRevenue={isRevenue}
          showPercent={showPercent}
        />
      </CursorTooltipPopover>
    </>
  );
};

export const ChannelPerformanceBars = ({
  data,
  isRevenue,
  hierarchyLevel,
  onChannelClick
}: ChannelPerformanceBarsProps) => {
  const [sortOption, setSortOption] = useState<SortOption>(SortOption.CONTRIB_DESC);
  const [valueDisplay, setValueDisplay] = useState<ValueDisplay>(ValueDisplay.ACTUAL);
  const [showCI, setShowCI] = useState(false);
  const [showBaseline, setShowBaseline] = useState(false);
  const [showDark, setShowDark] = useState(false);

  const baselineTotal = useMemo(
    () => data.actualVsPredictedT12M.reduce((s, r) => s + r.baselinePrediction, 0),
    [data.actualVsPredictedT12M]
  );

  const ciPct =
    data.modelInfo.confidenceLevel > 0 ? Math.round(data.modelInfo.confidenceLevel * 100) : null;
  const ciToggleLabel =
    ciPct !== null ? `Show ${ciPct}% credible intervals` : 'Show credible intervals';

  const efficiencyLabel = isRevenue ? 'ROAS' : 'CPA';
  const showPercent = valueDisplay === ValueDisplay.PERCENT;
  const kpiLabel = isRevenue ? 'Revenue' : 'Conv.';

  const allChannels = useMemo(
    () =>
      aggregateDualChannels(data.actualized.cptbT12M, data.raw.cptbT12M, hierarchyLevel, isRevenue),
    [data, hierarchyLevel, isRevenue]
  );

  const hasDarkChannels = useMemo(() => allChannels.some(c => c.isDark), [allChannels]);

  const dual = useMemo(() => {
    const channels = showDark ? allChannels : allChannels.filter(c => !c.isDark);

    if (!showBaseline || baselineTotal <= 0) return channels;

    const baseline: DualChannel = {
      groupKey: BASELINE_GROUP_KEY,
      displayName: BASELINE_DISPLAY_NAME,
      channel: BASELINE_GROUP_KEY,
      platform: '',
      mediaType: '',
      isDark: false,
      hasIncrementalityTest: false,
      directSpend: 0,
      actualizedSpend: 0,
      directContribution: baselineTotal,
      actualizedContribution: baselineTotal,
      actualizedContributionCiLo: 0,
      actualizedContributionCiHi: 0,
      directSpendShare: 0,
      actualizedSpendShare: 0,
      directContributionShare: 0,
      actualizedContributionShare: 0,
      efficiencyActualized: null,
      efficiencyDirect: null
    };

    const all = [...channels, baseline];
    const totalDirectContrib = all.reduce((s, c) => s + c.directContribution, 0);
    const totalActualizedContrib = all.reduce((s, c) => s + c.actualizedContribution, 0);
    return all.map(c => ({
      ...c,
      directContributionShare:
        totalDirectContrib > 0 ? (c.directContribution / totalDirectContrib) * 100 : 0,
      actualizedContributionShare:
        totalActualizedContrib > 0 ? (c.actualizedContribution / totalActualizedContrib) * 100 : 0
    }));
  }, [allChannels, showDark, showBaseline, baselineTotal]);

  const sorted = useMemo(
    () => [...dual].sort((a, b) => compareForSort(a, b, sortOption)),
    [dual, sortOption]
  );

  // Conversion factor from absolute contribution value → share-of-total percentage,
  // used to render CI bounds on the same scale as the share-mode bars.
  const contributionShareScale = useMemo(() => {
    const total = dual.reduce((s, c) => s + c.actualizedContribution, 0);
    return total > 0 ? 100 / total : 0;
  }, [dual]);

  const { maxSpend, maxContribution } = useMemo(() => {
    let mSpend = 0;
    let mContribution = 0;
    dual.forEach(c => {
      if (showPercent) {
        mSpend = Math.max(mSpend, c.directSpendShare, c.actualizedSpendShare);
        mContribution = Math.max(
          mContribution,
          c.directContributionShare,
          c.actualizedContributionShare
        );
        if (showCI) {
          mContribution = Math.max(
            mContribution,
            c.actualizedContributionCiHi * contributionShareScale
          );
        }
      } else {
        mSpend = Math.max(mSpend, c.directSpend, c.actualizedSpend);
        mContribution = Math.max(mContribution, c.directContribution, c.actualizedContribution);
        if (showCI) {
          mContribution = Math.max(mContribution, c.actualizedContributionCiHi);
        }
      }
    });
    // Percent mode: spend share and contribution share are both 0–100 percentages,
    // directly comparable as proportions — share the scale.
    // Actual mode + revenue KPI: both are dollars — share the scale so a $9M spend bar
    // reads visibly longer than a $5M revenue bar.
    // Actual mode + conversion KPI: units differ (dollars vs counts), keep separate.
    if (showPercent || isRevenue) {
      const shared = Math.max(mSpend, mContribution);
      return { maxSpend: shared, maxContribution: shared };
    }
    return { maxSpend: mSpend, maxContribution: mContribution };
  }, [dual, isRevenue, showPercent, showCI, contributionShareScale]);

  const sortOptions = useMemo(
    () => [
      { value: SortOption.SPEND_DESC, label: 'Spend (high to low)' },
      { value: SortOption.SPEND_ASC, label: 'Spend (low to high)' },
      { value: SortOption.CONTRIB_DESC, label: 'Contribution (high to low)' },
      { value: SortOption.CONTRIB_ASC, label: 'Contribution (low to high)' },
      { value: SortOption.EFFICIENCY_DESC, label: `${efficiencyLabel} (high to low)` },
      { value: SortOption.EFFICIENCY_ASC, label: `${efficiencyLabel} (low to high)` },
      { value: SortOption.ALPHA_ASC, label: 'Alphabetically (A → Z)' },
      { value: SortOption.ALPHA_DESC, label: 'Alphabetically (Z → A)' }
    ],
    [efficiencyLabel]
  );

  return (
    <div className='channel-performance-bars'>
      <div className='channel-performance-bars-toolbar'>
        <div className='channel-performance-bars-legend'>
          <span className='channel-performance-bars-legend-item'>
            <span className='channel-performance-bars-legend-swatch spend' />
            {showPercent ? 'Spend %' : 'Spend'}
          </span>
          <span className='channel-performance-bars-legend-item'>
            <span className='channel-performance-bars-legend-swatch effect' />
            {showPercent ? `${kpiLabel} %` : kpiLabel}
          </span>
          <span className='channel-performance-bars-legend-item'>
            <span className='channel-performance-bars-legend-swatch added' />
            {TWO_STAGE_ADDED_LABEL}
          </span>
          <span className='channel-performance-bars-legend-item'>
            <span className='channel-performance-bars-legend-swatch reduced' />
            {TWO_STAGE_REDUCED_LABEL}
          </span>
        </div>
        <div className='channel-performance-bars-sort'>
          <Dropdown<SortOption>
            overlayPlacement={Placement.RIGHT}
            size={Size.SM}
            value={sortOption}
            options={sortOptions}
            onChange={setSortOption}
            dropdownMenuClassName='align-right'
          />
          <Dropdown<ValueDisplay>
            overlayPlacement={Placement.RIGHT}
            size={Size.SM}
            value={valueDisplay}
            options={VALUE_DISPLAY_OPTIONS}
            onChange={setValueDisplay}
            dropdownMenuClassName='align-right'
          />
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className='channel-performance-bars-empty'>No channels in the selected window.</div>
      ) : (
        <div className='cpb-rows'>
          {sorted.map(channel => (
            <ChannelRow
              key={channel.groupKey}
              channel={channel}
              maxSpend={maxSpend}
              maxContribution={maxContribution}
              hierarchyLevel={hierarchyLevel}
              isRevenue={isRevenue}
              showPercent={showPercent}
              showCI={showCI}
              contributionShareScale={contributionShareScale}
              onClick={() => onChannelClick(channel.groupKey)}
            />
          ))}
        </div>
      )}

      <div className='channel-performance-bars-footer'>
        <IncrementalityNote />
        <ToggleSwitch
          label={BASELINE_TOGGLE_LABEL}
          checked={showBaseline}
          onChange={setShowBaseline}
        />
        <ToggleSwitch label={ciToggleLabel} checked={showCI} onChange={setShowCI} />
        <ToggleSwitch
          label={DARK_CHANNEL_TOGGLE_LABEL}
          checked={showDark}
          onChange={setShowDark}
          disabled={!hasDarkChannels}
        />
      </div>
    </div>
  );
};
